// Field helpers shared by the /finance/* editor dialogs (income editors, share
// grant fields, share-sale editor): "?" hints on captions, error hosts and a
// reader that parses typed inputs while collecting human-readable errors.
import { setInfoBox } from '../components/info-box.js';
import { captionWithHint, createHint } from '../components/hint.js';
import { money, parseMoney } from '../model/finance/money.js';
import { parsePercent } from '../model/finance/income-view.js';

/**
 * Add a "?" hint to a field: on the caption line of a captioned `<label>` (the
 * caption text node becomes a `captionWithHint`, so `setCaption` can still
 * change it), or at the end of a checkbox row.
 */
export function withHint(field, text) {
  if (!text) return field;
  if (field.classList.contains('checkbox-row')) {
    field.append(' ', createHint(text));
    return field;
  }
  const first = field.firstChild;
  if (first && first.nodeType === Node.TEXT_NODE) {
    first.replaceWith(captionWithHint(first.textContent, text));
  } else {
    field.prepend(captionWithHint('', text));
  }
  return field;
}

/** Change a field's caption text (works with or without a hint). */
export function setCaption(field, text) {
  const span = field.querySelector('.field-caption-text');
  if (span) span.textContent = text;
  else field.firstChild.textContent = text;
}

/** An info-box mount for a dialog's errors. */
export function errorHost() {
  const el = document.createElement('div');
  el.className = 'form-error-host';
  return el;
}

/** Show `errors` in `host`; true when there were any (keep the dialog open). */
export function showErrors(host, errors) {
  if (!errors.length) return false;
  setInfoBox(host, 'error', errors.join(' '));
  return true;
}

/** Reads typed inputs, collecting human-readable errors. */
export function reader() {
  const errors = [];
  return {
    errors,
    /**
     * Cents; the currency defaults to the money input's own (`data-currency`),
     * else AUD. `signed` allows a negative amount (e.g. a capital loss).
     */
    money(
      input,
      label,
      { required = false, signed = false, currency = input.dataset.currency ?? 'AUD' } = {},
    ) {
      const raw = input.value.trim();
      if (!raw) {
        if (required) errors.push(`${label} is required.`);
        return null;
      }
      const cents = parseMoney(raw, currency);
      if (cents === null || (!signed && cents < 0)) {
        errors.push(`${label} must be an amount, e.g. 1234.56.`);
        return null;
      }
      return cents;
    },
    /** A money value `{ amount (cents), currency }` in the input's currency (see `money`). */
    amount(input, label, opts = {}) {
      const currency = opts.currency ?? input.dataset.currency ?? 'AUD';
      const cents = this.money(input, label, { ...opts, currency });
      return cents === null ? null : money(cents, currency);
    },
    /** A per-share price value `{ amount (decimal), currency }` (see `decimal`). */
    price(input, label, opts = {}) {
      const n = this.decimal(input, label, opts);
      return n === null ? null : money(n, input.dataset.currency ?? 'AUD');
    },
    percent(input, label) {
      const rate = parsePercent(input.value);
      if (Number.isNaN(rate)) {
        errors.push(`${label} must be a percentage, e.g. 12.`);
        return null;
      }
      return rate;
    },
    int(input, label, { min = 0, max = Infinity } = {}) {
      const n = Number(input.value);
      if (!Number.isInteger(n) || n < min || n > max) {
        errors.push(
          `${label} must be a whole number${Number.isFinite(max) ? ` from ${min} to ${max}` : ''}.`,
        );
        return null;
      }
      return n;
    },
    /** A positive decimal (commas allowed); blank → null (or an error when required). */
    decimal(input, label, { required = false } = {}) {
      const raw = input.value.trim().replace(/,/g, '');
      if (!raw) {
        if (required) errors.push(`${label} is required.`);
        return null;
      }
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) {
        errors.push(`${label} must be a number above zero, e.g. 213.5.`);
        return null;
      }
      return n;
    },
  };
}
